import { useEffect, useState } from "react";
import { toast } from "sonner";
import { GripVertical, Eye, EyeOff, RotateCcw } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

export const applyPrefs = (items, prefs) => {
  const order = prefs?.order || [];
  const rank = (to) => { const i = order.indexOf(to); return i === -1 ? order.length + items.findIndex((x) => x.to === to) : i; };
  return [...items].sort((a, b) => rank(a.to) - rank(b.to));
};

const testKey = (to) => to.replace(/^\//, "").replace(/\//g, "-");

export function MenuEditor({ open, onClose, items }) {
  const { user, refresh } = useAuth();
  const [list, setList] = useState([]);
  const [hidden, setHidden] = useState([]);
  const [drag, setDrag] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setList(applyPrefs(items, user.menu_prefs));
    setHidden(user.menu_prefs?.hidden || []);
  }, [open]);

  const move = (from, to) => {
    if (from === to || to < 0 || to >= list.length) return;
    const next = [...list];
    const [x] = next.splice(from, 1);
    next.splice(to, 0, x);
    setList(next);
  };
  const toggle = (to) => setHidden((h) => (h.includes(to) ? h.filter((x) => x !== to) : [...h, to]));
  const save = async (prefs) => {
    setBusy(true);
    try { await api.put("/auth/me/menu-prefs", prefs); await refresh(); toast.success("Menü tercihleri kaydedildi"); onClose(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setBusy(false);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md max-h-[90vh] flex flex-col" data-testid="menu-editor-dialog">
        <DialogHeader>
          <DialogTitle>Menüyü düzenle</DialogTitle>
          <DialogDescription>Sürükleyerek sıralayın, göz simgesiyle gizleyin. Ayar yalnızca sizin hesabınıza uygulanır.</DialogDescription>
        </DialogHeader>
        <div className="flex-1 overflow-y-auto -mx-1 px-1 space-y-1" data-testid="menu-editor-list">
          {list.map((m, i) => {
            const off = hidden.includes(m.to);
            const fixed = m.to === "/panel";
            return (
              <div key={m.to} data-idx={i} data-testid={`menu-editor-item-${testKey(m.to)}`}
                className={`flex items-center gap-2 rounded-xl border px-2 py-2 bg-white transition-[opacity,box-shadow] ${drag === i ? "shadow-lg ring-2 ring-emerald-300" : "border-slate-200"} ${off ? "opacity-50" : ""}`}>
                <span data-testid={`menu-editor-grip-${testKey(m.to)}`} className="touch-none cursor-grab active:cursor-grabbing p-1 -m-1 shrink-0"
                  onPointerDown={(e) => { e.preventDefault(); e.currentTarget.setPointerCapture(e.pointerId); setDrag(i); }}
                  onPointerMove={(e) => {
                    if (drag === null) return;
                    const box = e.currentTarget.closest("[data-testid=menu-editor-list]");
                    const r = box.getBoundingClientRect();
                    if (e.clientY < r.top + 40) box.scrollTop -= 12;
                    else if (e.clientY > r.bottom - 40) box.scrollTop += 12;
                    const row = document.elementFromPoint(e.clientX, e.clientY)?.closest("[data-idx]");
                    const to = row ? Number(row.dataset.idx) : drag;
                    if (to !== drag) { move(drag, to); setDrag(to); }
                  }}
                  onPointerUp={() => setDrag(null)} onPointerCancel={() => setDrag(null)}>
                  <GripVertical className="w-4 h-4 text-slate-400" />
                </span>
                <m.icon className="w-4 h-4 text-slate-600 shrink-0" />
                <span className="flex-1 text-sm truncate">{m.label}</span>
                <button aria-label="Yukarı taşı" data-testid={`menu-editor-up-${testKey(m.to)}`} disabled={i === 0} onClick={() => move(i, i - 1)} className="px-1.5 text-slate-400 hover:text-slate-800 text-xs disabled:opacity-20">▲</button>
                <button aria-label="Aşağı taşı" data-testid={`menu-editor-down-${testKey(m.to)}`} disabled={i === list.length - 1} onClick={() => move(i, i + 1)} className="px-1.5 text-slate-400 hover:text-slate-800 text-xs disabled:opacity-20">▼</button>
                <button aria-label={off ? "Göster" : "Gizle"} disabled={fixed} title={fixed ? "Genel Bakış gizlenemez" : undefined} data-testid={`menu-editor-toggle-${testKey(m.to)}`}
                  onClick={() => toggle(m.to)} className="p-1 rounded-lg hover:bg-slate-100 disabled:opacity-30">
                  {off ? <EyeOff className="w-4 h-4 text-slate-500" /> : <Eye className="w-4 h-4 text-emerald-700" />}
                </button>
              </div>
            );
          })}
        </div>
        <div className="flex gap-2 pt-2">
          <Button variant="outline" data-testid="menu-editor-reset" disabled={busy} onClick={() => save({ order: [], hidden: [] })}><RotateCcw className="w-4 h-4 mr-1.5" />Varsayılana dön</Button>
          <Button className="flex-1" data-testid="menu-editor-save" disabled={busy} onClick={() => save({ order: list.map((x) => x.to), hidden })}>{busy ? "Kaydediliyor…" : "Kaydet"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
